import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { FormsService } from 'src/app/applicationModules/eSignature/services/forms.service';

interface MatchRecord {
  matchID: number;
  offenderId?: number | null;
  registrationId?: string | null;
  fullName?: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  dateOfBirth?: string | null;
  sex?: string;
  age?: number | null;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  latitude?: number | null;
  longitude?: number | null;
  registrationType?: string;
  registrationStatus?: string;
  riskLevel?: string;
  tierLevel?: string;
  sourceState?: string;
  convictionOffense?: string;
  convictionDate?: string | null;
  registrationDate?: string | null;
  photoUrl?: string;
  distanceMiles?: number | null;
  matchScore: number;
  matchReasons?: string;
}

interface HistoryRecord {
  backgroundCheckID: number;
  source?: string;
  searchParamsJson?: string;
  totalMatches: number;
  riskLabel?: string;
  riskReason?: string;
  decisionStatus: number;
  decisionUpdatedOn?: string | null;
  selectedMatchID?: number | null;
  checkedOn: string;
  checkedByName?: string;
  matches: MatchRecord[];
}

interface NoteRecord {
  noteID: number;
  noteText: string;
  createdOn: string;
  createdByName?: string;
}

const DECISION = {
  PENDING_REVIEW:           1,
  CLEARED:                  2,
  MATCH_FOUND_BLOCKED:      3,
  MATCH_FOUND_OVERRIDE:     4
};

@Component({
  selector: 'app-background-check',
  templateUrl: './background-check.component.html',
  styleUrls: ['./background-check.component.scss']
})
export class BackgroundCheckComponent implements OnInit {

  readonly DECISION = DECISION;

  // ─── Navigation ──────────────────────────────────────────────────────────
  sections: string[] = ['Intake', 'Search Results', 'Decision', 'History', 'Notes'];
  activeSection: string = 'Intake';

  // ─── Search form ─────────────────────────────────────────────────────────
  searchForm: FormGroup;
  isRunning = false;

  // ─── Current run state ───────────────────────────────────────────────────
  currentRun: {
    backgroundCheckID?: number;
    totalMatches: number;
    riskLabel?: string;
    riskReason?: string;
    decisionStatus: number;
    decisionUpdatedOn?: string | null;
    selectedMatchID?: number | null;
    checkedOn?: string;
    checkedByName?: string;
    matches: MatchRecord[];
    providerError?: string;
  } = { totalMatches: 0, decisionStatus: DECISION.PENDING_REVIEW, matches: [] };

  // Set to true while history is being loaded for the very first time.
  // Prevents the component from showing "no run yet" before we know.
  isHydratingFromHistory: boolean = true;

  selectedMatch: MatchRecord | null = null;
  matchSearch = '';

  // ─── Decision panel ──────────────────────────────────────────────────────
  isUpdatingDecision = false;

  // ─── History ─────────────────────────────────────────────────────────────
  historyRecords: HistoryRecord[] = [];
  isLoadingHistory = false;
  selectedHistory: HistoryRecord | null = null;

  // ─── Notes ───────────────────────────────────────────────────────────────
  notes: NoteRecord[] = [];
  newNoteText = '';
  isLoadingNotes = false;
  isSubmittingNote = false;
  readonly NOTE_MAX_LENGTH = 2000;

  // ─── Filter option lists (mirror the API documentation) ──────────────────
  readonly riskLevelOptions     = ['Low', 'Medium', 'High'];
  readonly statusOptions        = ['active', 'inactive'];
  readonly registrationOptions  = ['Sexual Predator', 'Sexually Violent Predator', 'Tier I', 'Tier II', 'Tier III'];
  readonly sortByOptions        = ['name', 'last_seen', 'date_added', 'conviction_date'];
  readonly sortOrderOptions     = ['asc', 'desc'];
  readonly stateOptions = [
    'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY',
    'LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND',
    'OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'
  ];

  constructor(
    public dialogRef: MatDialogRef<BackgroundCheckComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: FormBuilder,
    private formsService: FormsService,
    private toastr: ToastrService
  ) {
    this.searchForm = this.fb.group({
      q:                 [''],
      first_name:        ['', Validators.required],
      last_name:         [''],
      date_of_birth:     [''],
      zip_code:          [''],
      city:              [''],
      state:             [''],
      risk_level:        [''],
      status:            [''],
      registration_type: [''],
      dob_year:          [null],
      age_min:           [null],
      age_max:           [null],
      latitude:          [null],
      longitude:         [null],
      radius_miles:      [null],
      sort_by:           ['name'],
      sort_order:        ['asc'],
      page:              [1],
      per_page:          [100]
    });
  }

  ngOnInit(): void {
    this.prefillFromSelectedRecord();
    this.loadHistory();
  }

  // ─── Case identity envelope (mirrors Waystar pattern) ────────────────────
  private get caseSource(): string {
    return this.data?.source || 'referral';
  }

  private buildCaseRef(): any {
    const rec = this.data?.selectedRecord || {};
    return {
      source:         this.caseSource,
      referralID:     rec.referralID ?? null,
      coreID:         rec.coreID     ?? null,
      organizationID: this.data?.organizationID,
      officeID:       this.data?.officeID,
      userID:         this.data?.userID
    };
  }

  private hasCaseRef(): boolean {
    const c = this.buildCaseRef();
    return !!(c.referralID || c.coreID);
  }

  // ─── Section nav ─────────────────────────────────────────────────────────
  selectSection(s: string): void {
    this.activeSection = s;
    if (s === 'History') this.loadHistory();
    if (s === 'Notes')   this.loadNotes();
  }

  // Splits a single name string into first / middle / last. Handles:
  //   "DUNCAN, SUSAN RENEE"  (Last, First Middle — comma format)
  //   "SUSAN RENEE DUNCAN"   (First Middle Last — last token is the last name)
  //   "SUSAN DUNCAN"         (First Last)
  //   "MADONNA"              (single token → first name only)
  // Middle is separated out so it never gets lumped into the first or last name
  // (the registry matches on those). Mirrors the Waystar intake parser.
  private parseName(raw: any): { first: string; middle: string; last: string } {
    const out = { first: '', middle: '', last: '' };
    const text = (raw || '').toString().trim().replace(/\s+/g, ' ');
    if (!text) return out;

    if (text.includes(',')) {
      // "Last, First Middle"
      const [lastPart, ...rest] = text.split(',');
      out.last = (lastPart || '').trim();
      const firstParts = rest.join(',').trim().split(' ').filter(p => p);
      out.first  = firstParts[0] || '';
      out.middle = firstParts.slice(1).join(' ');
      return out;
    }

    const parts = text.split(' ').filter(p => p);
    if (parts.length === 1) {
      out.first = parts[0];
    } else if (parts.length === 2) {
      out.first = parts[0];
      out.last  = parts[1];
    } else {
      out.first  = parts[0];
      out.last   = parts[parts.length - 1];
      out.middle = parts.slice(1, -1).join(' ');
    }
    return out;
  }

  // ─── Pre-populate from referral/Core ─────────────────────────────────────
  private prefillFromSelectedRecord(): void {
    const rec = this.data?.selectedRecord;
    if (!rec) return;

    // Prefer discrete name fields; otherwise split the combined name string.
    // Only first/last are used — the middle name is intentionally dropped so it
    // never ends up inside the first or last name.
    let firstName = rec.firstName || '';
    let lastName  = rec.lastName  || '';
    if ((!firstName || !lastName) && rec.name) {
      const parsed = this.parseName(rec.name);
      if (!firstName) firstName = parsed.first;
      if (!lastName)  lastName  = parsed.last;
    }

    // Material datepicker binds to a Date instance, not a string — so hand it
    // a Date (or null) here and let normalizeDob() serialize it on submit.
    let dob: Date | null = null;
    const rawDob = rec.dob || rec.dateOfBirth;
    if (rawDob) {
      const d = new Date(rawDob);
      if (!isNaN(d.getTime())) dob = d;
    }

    this.searchForm.patchValue({
      first_name:    firstName,
      last_name:     lastName,
      date_of_birth: dob,
      zip_code:      rec.zipCode || rec.zip || '',
      city:          rec.city    || '',
      state:         rec.state   || ''
    });
  }

  // ─── Run check ───────────────────────────────────────────────────────────
  runCheck(): void {
    if (this.searchForm.invalid) {
      this.toastr.warning('First and last name are required.', 'Validation');
      this.searchForm.markAllAsTouched();
      return;
    }
    if (!this.hasCaseRef()) {
      this.toastr.error('No case identifier (referralID or coreID) — cannot run check.', 'Error');
      return;
    }

    const f = this.searchForm.value;
    const dobIso = this.normalizeDob(f.date_of_birth);

    const req = {
      ...this.buildCaseRef(),
      subjectFirstName:    f.first_name || null,
      subjectLastName:     f.last_name  || null,
      subjectDateOfBirth:  dobIso,
      subjectZipCode:      f.zip_code   || null,
      subjectLatitude:     this.numOrNull(f.latitude),
      subjectLongitude:    this.numOrNull(f.longitude),
      search: {
        q:                 this.strOrNull(f.q),
        firstName:         this.strOrNull(f.first_name),
        lastName:          this.strOrNull(f.last_name),
        zipCode:           this.strOrNull(f.zip_code),
        city:              this.strOrNull(f.city),
        state:             this.strOrNull(f.state),
        riskLevel:         this.strOrNull(f.risk_level),
        status:            this.strOrNull(f.status),
        registrationType:  this.strOrNull(f.registration_type),
        dobYear:           this.numOrNull(f.dob_year),
        ageMin:            this.numOrNull(f.age_min),
        ageMax:            this.numOrNull(f.age_max),
        latitude:          this.numOrNull(f.latitude),
        longitude:         this.numOrNull(f.longitude),
        radiusMiles:       this.numOrNull(f.radius_miles),
        sortBy:            this.strOrNull(f.sort_by),
        sortOrder:         this.strOrNull(f.sort_order),
        page:              this.numOrNull(f.page)     ?? 1,
        perPage:           this.numOrNull(f.per_page) ?? 100
      }
    };

    this.isRunning = true;
    this.selectedMatch = null;
    this.formsService.runBackgroundCheck(req).subscribe({
      next: (res) => {
        this.isRunning = false;
        const d = res?.data;
        if (!d) {
          this.toastr.error('Background check failed.', 'Error');
          return;
        }
        this.currentRun = {
          backgroundCheckID: d.backgroundCheckID,
          totalMatches:      d.totalMatches || 0,
          riskLabel:         d.riskLabel,
          riskReason:        d.riskReason,
          decisionStatus:    d.decisionStatus || DECISION.PENDING_REVIEW,
          checkedOn:         d.checkedOn,
          matches:           (d.matches || []) as MatchRecord[],
          providerError:     d.providerError
        };
        // Pre-focus on the strongest match so the reviewer's eye lands there.
        this.selectedMatch = this.currentRun.matches.length > 0 ? this.currentRun.matches[0] : null;
        if (d.providerError) {
          this.toastr.warning(d.providerError, 'Provider warning');
        } else {
          this.toastr.success(`Check complete — ${this.currentRun.totalMatches} candidate(s) returned.`, 'Background Check');
        }
        this.activeSection = 'Search Results';
        // Refresh history with this run included
        this.loadHistory(true);
      },
      error: () => {
        this.isRunning = false;
        this.toastr.error('Background check failed.', 'Error');
      }
    });
  }

  // ─── Match list helpers ──────────────────────────────────────────────────
  get filteredMatches(): MatchRecord[] {
    if (!this.matchSearch) return this.currentRun.matches;
    const q = this.matchSearch.toLowerCase();
    return this.currentRun.matches.filter(m =>
      (m.fullName || '').toLowerCase().includes(q) ||
      (m.address  || '').toLowerCase().includes(q) ||
      (m.city     || '').toLowerCase().includes(q) ||
      (m.state    || '').toLowerCase().includes(q) ||
      (m.zipCode  || '').toLowerCase().includes(q)
    );
  }

  selectMatch(m: MatchRecord): void {
    this.selectedMatch = this.selectedMatch?.matchID === m.matchID ? null : m;
    // On narrow viewports where the detail panel stacks below the grid,
    // scroll it into view so the user never has to hunt for the details.
    // On wide viewports the panel is sticky so no scrolling is needed and
    // this is a cheap no-op (the element is already in the viewport).
    if (this.selectedMatch) {
      setTimeout(() => {
        const el = document.getElementById('bgcMatchDetail');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 0);
    }
  }

  clearSelectedMatch(): void {
    this.selectedMatch = null;
  }

  // ─── Decision ────────────────────────────────────────────────────────────
  updateDecision(status: number): void {
    if (!this.currentRun.backgroundCheckID) {
      this.toastr.info('Run a check first.', '');
      return;
    }
    // Persist the offender the reviewer focused on so we can restore the
    // exact selection on re-open. Falls back to the highest-scoring match
    // when the reviewer didn't pick anyone explicitly.
    const focused = this.selectedMatch?.matchID
                 || this.currentRun.matches[0]?.matchID
                 || null;
    const req = {
      ...this.buildCaseRef(),
      backgroundCheckID: this.currentRun.backgroundCheckID,
      decisionStatus:    status,
      selectedMatchID:   focused
    };
    this.isUpdatingDecision = true;
    this.formsService.updateBackgroundCheckDecision(req).subscribe({
      next: (res) => {
        this.isUpdatingDecision = false;
        if (res?.data) {
          this.currentRun.decisionStatus    = status;
          this.currentRun.decisionUpdatedOn = new Date().toISOString();
          this.currentRun.selectedMatchID   = focused;
          this.toastr.success(`Decision saved — ${this.decisionLabel(status)}.`, 'Saved');
          this.loadHistory(true);
        } else {
          this.toastr.error('Failed to save decision.', 'Error');
        }
      },
      error: () => {
        this.isUpdatingDecision = false;
        this.toastr.error('Failed to save decision.', 'Error');
      }
    });
  }

  decisionLabel(s: number): string {
    switch (s) {
      case DECISION.CLEARED:              return 'Cleared';
      case DECISION.MATCH_FOUND_BLOCKED:  return 'Match Found — Blocked';
      case DECISION.MATCH_FOUND_OVERRIDE: return 'Match Found — Override Allowed';
      case DECISION.PENDING_REVIEW:       return 'Pending Review';
      default: return '';
    }
  }

  decisionBadgeClass(s: number): string {
    switch (s) {
      case DECISION.CLEARED:              return 'pill pill-decision-cleared';
      case DECISION.MATCH_FOUND_BLOCKED:  return 'pill pill-decision-blocked';
      case DECISION.MATCH_FOUND_OVERRIDE: return 'pill pill-decision-override';
      case DECISION.PENDING_REVIEW:       return 'pill pill-decision-pending';
      default: return 'pill pill-decision-default';
    }
  }

  // Maps the risk label string to the gradient banner class suffix.
  // (Kept in TS so the template stays Angular 9-parser friendly.)
  riskBannerClass(label: string | undefined | null): string {
    switch ((label || '').toLowerCase()) {
      case 'no match':         return 'bgc-risk-banner bgc-risk-banner-no-match';
      case 'possible match':   return 'bgc-risk-banner bgc-risk-banner-possible-match';
      case 'confirmed match':  return 'bgc-risk-banner bgc-risk-banner-confirmed-match';
      case 'high risk match':  return 'bgc-risk-banner bgc-risk-banner-high-risk-match';
      default:                 return 'bgc-risk-banner';
    }
  }

  riskLabelClass(label: string | undefined): string {
    switch ((label || '').toLowerCase()) {
      case 'no match':         return 'pill pill-risk-nomatch';
      case 'possible match':   return 'pill pill-risk-possible';
      case 'confirmed match':  return 'pill pill-risk-confirmed';
      case 'high risk match':  return 'pill pill-risk-high';
      default:                 return 'pill pill-risk-default';
    }
  }

  scoreClass(score: number): string {
    if (score >= 95) return 'score score-critical';
    if (score >= 90) return 'score score-high';
    if (score >= 60) return 'score score-medium';
    if (score >  0)  return 'score score-low';
    return 'score score-none';
  }

  // Pill color hint based on the provider's risk label. Keeps the high-risk
  // labels visually loud regardless of source capitalization.
  riskLevelClass(level?: string): string {
    const v = (level || '').trim().toLowerCase();
    if (!v) return 'tag-risk-unknown';
    if (v === 'high' || v === 'level 3' || v === 'tier 3') return 'tag-risk-high';
    if (v === 'medium' || v === 'moderate' || v === 'level 2' || v === 'tier 2') return 'tag-risk-medium';
    if (v === 'low' || v === 'level 1' || v === 'tier 1') return 'tag-risk-low';
    return 'tag-risk-unknown';
  }

  // Build a single address line that omits empty parts so we don't render
  // dangling commas like ",, 90210".
  formatAddress(m: MatchRecord | null | undefined): string {
    if (!m) return '';
    const cityState = [m.city, m.state].filter(Boolean).join(', ');
    const tail      = [cityState, m.zipCode].filter(Boolean).join(' ').trim();
    return [m.address, tail].filter(Boolean).join(', ').trim();
  }

  // Compose a display name from whichever parts the offender record carries.
  // Kept in TS rather than inlined in the template because Angular 10's
  // template expression parser doesn't reliably handle chained method calls
  // like .replace('  ', ' ').trim() with literal arguments.
  displayName(m: MatchRecord | null | undefined): string {
    if (!m) return '';
    if (m.fullName) return m.fullName;
    return [m.firstName, m.middleName, m.lastName]
      .filter(Boolean)
      .join(' ')
      .trim();
  }

  truncate(s: string | undefined | null, max: number): string {
    if (!s) return '';
    return s.length <= max ? s : s.substring(0, max - 1) + '…';
  }

  // ─── History ─────────────────────────────────────────────────────────────
  loadHistory(force: boolean = false): void {
    if (!this.hasCaseRef()) { this.isHydratingFromHistory = false; return; }
    this.isLoadingHistory = true;
    this.formsService.getBackgroundCheckHistory(this.buildCaseRef()).subscribe({
      next: (res) => {
        this.historyRecords = res?.data?.records || [];
        this.isLoadingHistory = false;
        // On first open: if a prior run exists, hydrate current state from it
        // and land on the most useful tab.
        if (this.isHydratingFromHistory) {
          this.isHydratingFromHistory = false;
          if (this.historyRecords.length > 0) {
            this.hydrateCurrentRunFromHistory(this.historyRecords[0]);
            this.chooseInitialSectionAfterHydrate();
          }
        }
      },
      error: () => {
        this.isLoadingHistory = false;
        this.isHydratingFromHistory = false;
      }
    });
  }

  // Restore the current-run state (matches + risk + decision) from a history
  // record so the user lands on a meaningful view, not a blank intake form.
  private hydrateCurrentRunFromHistory(h: HistoryRecord): void {
    this.currentRun = {
      backgroundCheckID: h.backgroundCheckID,
      totalMatches:      h.totalMatches,
      riskLabel:         h.riskLabel,
      riskReason:        h.riskReason,
      decisionStatus:    h.decisionStatus,
      decisionUpdatedOn: h.decisionUpdatedOn,
      selectedMatchID:   h.selectedMatchID,
      checkedOn:         h.checkedOn,
      checkedByName:     h.checkedByName,
      matches:           (h.matches || []) as MatchRecord[]
    };

    // Mirror the search params back into the intake form so the reviewer can
    // see (and re-run) what was searched for.
    if (h.searchParamsJson) {
      try { this.searchForm.patchValue(JSON.parse(h.searchParamsJson)); } catch { /* ignore */ }
    }

    // Restore the reviewer's focus: prefer the persisted SelectedMatchID, fall
    // back to the highest-scoring match (currentRun.matches[0] after sort).
    if (h.selectedMatchID) {
      const persisted = this.currentRun.matches.find(m => m.matchID === h.selectedMatchID);
      this.selectedMatch = persisted || (this.currentRun.matches[0] || null);
    } else if (this.currentRun.matches.length > 0) {
      this.selectedMatch = this.currentRun.matches[0];
    }
  }

  private chooseInitialSectionAfterHydrate(): void {
    if (!this.currentRun.backgroundCheckID) return;
    const pending = this.currentRun.decisionStatus === DECISION.PENDING_REVIEW;
    if (pending && this.currentRun.totalMatches > 0) {
      this.activeSection = 'Decision';
    } else {
      this.activeSection = 'Search Results';
      // The right rail opens already focused on the persisted/best match —
      // scroll the results grid so that same person's card is in view rather
      // than leaving the reviewer to hunt for it in a long list.
      this.scrollSelectedMatchIntoView();
    }
  }

  // Scroll the results canvas so the currently selected match's card is
  // brought into view near the top, aligning the mid panel with the offender
  // shown in the right detail rail. Deferred so the *ngIf'd cards exist in the
  // DOM first.
  private scrollSelectedMatchIntoView(): void {
    if (!this.selectedMatch) return;
    const matchID = this.selectedMatch.matchID;
    setTimeout(() => {
      const card = document.getElementById('bgcMatchCard-' + matchID);
      const container = document.querySelector('.bgc-main') as HTMLElement | null;
      if (!card || !container) return;
      const delta = card.getBoundingClientRect().top
                  - container.getBoundingClientRect().top - 24;
      container.scrollBy({ top: delta, behavior: 'smooth' });
    }, 200);
  }

  selectHistory(h: HistoryRecord): void {
    this.selectedHistory = this.selectedHistory?.backgroundCheckID === h.backgroundCheckID ? null : h;
  }

  loadHistoryIntoViewer(h: HistoryRecord): void {
    this.currentRun = {
      backgroundCheckID: h.backgroundCheckID,
      totalMatches:      h.totalMatches,
      riskLabel:         h.riskLabel,
      riskReason:        h.riskReason,
      decisionStatus:    h.decisionStatus,
      checkedOn:         h.checkedOn,
      matches:           h.matches || []
    };
    this.selectedMatch = null;
    if (h.searchParamsJson) {
      try { this.searchForm.patchValue(JSON.parse(h.searchParamsJson)); } catch {}
    }
    this.activeSection = 'Search Results';
    this.toastr.info(`Loaded run from ${this.formatDateTime(h.checkedOn)}`, 'History');
  }

  // ─── Notes ───────────────────────────────────────────────────────────────
  loadNotes(): void {
    if (!this.hasCaseRef()) return;
    this.isLoadingNotes = true;
    this.formsService.getBackgroundCheckNotes(this.buildCaseRef()).subscribe({
      next: (res) => {
        this.notes = (res?.data?.notes || []) as NoteRecord[];
        this.isLoadingNotes = false;
      },
      error: () => { this.isLoadingNotes = false; }
    });
  }

  submitNote(): void {
    if (!this.newNoteText.trim()) {
      this.toastr.warning('Note cannot be empty.', 'Validation');
      return;
    }
    if (!this.hasCaseRef()) return;
    this.isSubmittingNote = true;
    const req = { ...this.buildCaseRef(), noteText: this.newNoteText.trim() };
    this.formsService.addBackgroundCheckNote(req).subscribe({
      next: (res) => {
        this.isSubmittingNote = false;
        if (res?.data) {
          this.newNoteText = '';
          this.toastr.success('Note added.', 'Saved');
          this.loadNotes();
        } else {
          this.toastr.error('Failed to save note.', 'Error');
        }
      },
      error: () => {
        this.isSubmittingNote = false;
        this.toastr.error('Failed to save note.', 'Error');
      }
    });
  }

  // ─── Close ───────────────────────────────────────────────────────────────
  closeDialog(): void {
    this.dialogRef.close({
      decisionStatus: this.currentRun.decisionStatus,
      riskLabel:      this.currentRun.riskLabel
    });
  }

  // ─── Formatters ──────────────────────────────────────────────────────────
  formatDate(s?: string | null): string {
    if (!s) return '—';
    const d = new Date(s);
    return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
  }

  formatDateTime(s?: string | null): string {
    if (!s) return '—';
    const d = new Date(s);
    return isNaN(d.getTime()) ? '—' : `${d.toLocaleDateString('en-US')} ${d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
  }

  initials(name?: string): string {
    if (!name) return '?';
    return name.split(/\s+/).filter(Boolean).slice(0, 2).map(p => p[0].toUpperCase()).join('') || '?';
  }

  // ─── Internal helpers ────────────────────────────────────────────────────
  private strOrNull(v: any): string | null {
    if (v === undefined || v === null) return null;
    const s = String(v).trim();
    return s.length === 0 ? null : s;
  }

  private numOrNull(v: any): number | null {
    if (v === undefined || v === null || v === '') return null;
    const n = Number(v);
    return isNaN(n) ? null : n;
  }

  private normalizeDob(v: string): string | null {
    if (!v) return null;
    const d = new Date(v);
    if (isNaN(d.getTime())) return null;
    return d.toISOString();
  }
}
